"use client";
import CreateAccount from "@/app/layouts/standard/footer/createAccount";
import TrustBadges from "@/app/layouts/standard/footer/trustBadges";
import EmailSubscribe from "@/app/layouts/standard/footer/emailSubscribe";
import Link from "next/link";
import Net32LogoWhite from "public/next-public/common/net32-logo-white.svg";
import { ButtonHTMLAttributes, MouseEvent, useState } from "react";
import styles from "./standardPageFooterRevised.module.scss";
import TakeDownRequestsModal from "./takeDownRequestsModal";

interface StandardPageFooterRevisedProps {
  /** When true, the "Ready to get started" band and trust badges span the full page width (matches a full-width header). Otherwise their content is capped at the header's 1440px width. */
  fullWidth?: boolean;
}

const StandardPageFooterRevised = ({ fullWidth = false }: StandardPageFooterRevisedProps) => {
  const [showTakeDownModal, setShowTakeDownModal] = useState(false);
  const footerLinks: Record<string, FooterLink[]> = {
    Company: [
      { name: "About Us", href: "/about-us" },
      { name: "Why Shop Net32?", href: "/why-shop-net32" },
      { name: "Customer Reviews", href: "/about-us/reviews" },
      { name: "Sell on Net32", href: "/vendor/become-a-vendor" },
      { name: "All Vendors", href: "/vendor" },
      { name: "Blog", href: "https://www.net32.com/modern-practice/" },
      { name: "Careers", href: "/careers" },
    ],
    "My Account": [
      { name: "Account Details", href: "/account" },
      { name: "My Orders", href: "/account/orders" },
      { name: "Auto Order", href: "/account/orders/auto-order" },
      { name: "Reorder", href: "/reorder" },
      { name: "Shopping List", href: "/shopping-list" },
      { name: "Review a Product", href: "/review/products" },
      { name: "Review a Vendor", href: "/review/vendor" },
      { name: "Privacy Preferences", href: "/user-privacy-settings" },
    ],
    Shop: [
      { name: "All Categories", href: "/ec/dental-supplies" },
      { name: "Top Sellers", href: "/top-sellers" },
      { name: "New Arrivals", href: "/new-arrivals" },
      { name: "Weekly Specials", href: "/weekly-specials" },
      { name: "Promotions", href: "/mfr-promotions" },
      { name: "Shopping Cart", href: "/shopping-cart" },
      { name: "Checkout", href: "/checkout" },
    ],
    Help: [
      { name: "Contact Us", href: "/contact-us" },
      {
        name: "Help Center",
        href: "https://support.net32.com/hc/en-us/categories/28102936952851-For-Customers",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "Order Tracking",
        href: "https://support.net32.com/hc/en-us/articles/28137253964179-How-can-I-track-my-order",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "Order Cancellation",
        href: "https://support.net32.com/hc/en-us/articles/28137172636307-How-can-I-cancel-an-order",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "Return Policy",
        href: "https://support.net32.com/hc/en-us/articles/28136939501203-Is-my-product-eligible-for-a-return",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "Shipping Information",
        href: "https://support.net32.com/hc/en-us/articles/41919340147987-How-Does-Shipping-Work-on-Net32",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "Review giveaway rules",
        href: "/giveaways/ratings-and-reviews",
      },
      {
        name: "Takedown Request",
        href: "#",
        rel: "nofollow",
        buttonAttributes: { "aria-haspopup": "dialog", "data-takedown-request-link": "footer" },
        onClick: (event) => {
          event.preventDefault();
          setShowTakeDownModal(true);
        },
      },
    ],
  };

  const renderLinks = (links: FooterLink[]) =>
    links.map((link, i) => {
      const isExternal = link.href.startsWith("http://") || link.href.startsWith("https://");

      return (
        <li key={i}>
          {link.onClick ? (
            <button {...link.buttonAttributes} type="button" onClick={link.onClick} className={styles.footerButtonLink}>
              {link.name}
            </button>
          ) : isExternal ? (
            <a
              href={link.href}
              rel={link.rel || "noopener noreferrer"}
              target={link.target || (isExternal ? "_blank" : undefined)}
            >
              {link.name}
            </a>
          ) : (
            //please don't prefetch these links (it causes cookies to get deleted)
            <Link href={link.href} rel={link.rel} prefetch={false}>
              {link.name}
            </Link>
          )}
        </li>
      );
    });

  return (
    <>
      <CreateAccount fullWidth={fullWidth} />
      <TrustBadges fullWidth={fullWidth} />
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerContent}>
            <p className={styles.helpText}>
              <span>
                Need help? Call <span className={styles.helpTextTollFree}>toll free</span>
              </span>
              <span>
                <a href="tel:8005171997" className={styles.helpTextPhoneNumber}>
                  (800) 517-1997
                </a>{" "}
              </span>
              <span>Extended hours M-F 8am-8pm ET</span>
            </p>
            <div className={styles.footerTopSection}>
              <div className={styles.footerLinkColumns}>
                <div className={styles.footerSection}>
                  <h5>Company</h5>
                  <ul>{renderLinks(footerLinks.Company)}</ul>
                </div>
                <div className={styles.footerSection}>
                  <h5>My Account</h5>
                  <ul>{renderLinks(footerLinks["My Account"])}</ul>
                </div>
                <div className={styles.footerSection}>
                  <h5>Shop</h5>
                  <ul>{renderLinks(footerLinks.Shop)}</ul>
                </div>
                <div className={styles.footerSection}>
                  <h5>Help</h5>
                  <ul>{renderLinks(footerLinks.Help)}</ul>
                </div>
              </div>
              <div className={styles.emailSubscribeWrapper}>
                <EmailSubscribe />
              </div>
            </div>
            <TakeDownRequestsModal showTakeDownModal={showTakeDownModal} setShowTakeDownModal={setShowTakeDownModal} />
          </div>
        </div>
        <div className={styles.footerBottom}>
          <div className={styles.footerBottomContainer}>
            <Net32LogoWhite alt="Net32 Logo" className={styles.logo} />
            <div className={styles.legalLinksAndCopy}>
              <div className={`d-flex flex-wrap gap-3 my-2 ${styles.legalLinks}`}>
                <a href="/privacy-security">Privacy Policy</a>
                <a href="/terms-of-service">Terms of Service</a>
                <a href="/privacy-security#12">Legal</a>
                <a href="/site-map">Site Map</a>
              </div>
            </div>
            <p className="mb-0">
              {`Copyright © 2025 Net32 Inc. All rights reserved. Net32's proprietary comparison shopping software is protected by U.S. Patent #7107226.`}
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default StandardPageFooterRevised;

interface FooterLink {
  name: string;
  href: string;
  rel?: string;
  target?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  buttonAttributes?: ButtonHTMLAttributes<HTMLButtonElement> & Record<`data-${string}`, string>;
}
